import type { ReactNode } from 'react';
import { Box, Card, Heading, Icon, Paragraph } from '@zillow/constellation-10';
import type { SystemStyleObject } from '@/styled-system/types';
import type { StaticImageData } from 'next/image';
import Image from 'next/image';

type EmptyStateProps = {
  heading?: string;
  headingLevel?: 2 | 3;
  body?: string;
  illustration?: StaticImageData;
  icon?: ReactNode;
  cta?: ReactNode;
  withCard?: boolean;
  bodyMaxWidth?: string;
  css?: SystemStyleObject;
};

export default function EmptyState({
  heading,
  headingLevel = 2,
  body,
  illustration,
  icon,
  cta,
  withCard = false,
  bodyMaxWidth,
  css: cssProp,
}: EmptyStateProps) {
  const content = (
    <>
      {illustration && (
        <Box aria-hidden="true">
          <Image src={illustration} alt="" width={120} height={120} />
        </Box>
      )}
      {icon && (
        <Box aria-hidden="true">
          <Icon render={icon} size="md" css={{ color: 'icon.subtle' }} />
        </Box>
      )}
      {heading && (
        <Heading level={headingLevel} textStyle="heading-xs">
          {heading}
        </Heading>
      )}
      {body && <Paragraph css={{ maxWidth: bodyMaxWidth }}>{body}</Paragraph>}
      {cta}
    </>
  );

  const sharedStyles: SystemStyleObject = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 'default',
    ...cssProp,
  };

  if (withCard) {
    return (
      <Card
        tone="neutral"
        elevated={false}
        css={{
          ...sharedStyles,
          paddingInline: 'looser',
          paddingBlock: 'looser',
        }}
      >
        {content}
      </Card>
    );
  }

  return <Box css={sharedStyles}>{content}</Box>;
}
